import { Avatar } from 'components/Avatar';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ItemDetails } from 'components/ItemDetails';
import { navigateScreenAware } from 'components/Navigation/navigationRef';
import { ModalScreens, Screens, Stacks } from 'components/Navigation/types';
import { PageHeader } from 'components/PageHeader';
import { Page } from 'components/Page';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { filterArrayEmpty } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { Platform } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  doSetupAseLoad,
  REMOTE_ASESETUP_LOAD,
  doFieldSettingsLoad,
  doSettingOrganisationSelect,
  doSetupBillingOpen,
  doSettingsAseChecksLoad,
  doSetupApiOpen,
  doSetupEngageOpen,
  doSetupBillingAdminOpen,
  doSetupSsoOpen,
  eventSelectScreenSelection,
  doSettingOrganisationSelectStart,
  doSetupOrganisationOpen,
  NAVIGATE_MODAL,
  doSettingsComplianceAndAssessmentTypesLoad,
  doSettingsSeveritiesOpen,
} from 'store/actions';
import {
  selectUserIsSupport,
  selectUserSelectedOrganisation,
} from 'store/auth/selectors';
import { GapSmall } from 'components/GapSmall';
import { selectHasSidebarNavigation } from 'store/app/selectors';

export const SettingsExplorerScreen: React.FC = () => {
  const dispatch = useDispatch();
  const hasSidebarNavigation = useSelector(selectHasSidebarNavigation);

  const eventKeyChangeOrganisation = 'settingsExplorerChooseOrganisation';

  const organisation = useSelector(selectUserSelectedOrganisation);
  const isSupport = useSelector(selectUserIsSupport);
  const canManageIntegrations =
    isSupport && !!organisation?.userFeatures.manageIntegrations;

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (event) => {
        if (event.payload.key === eventKeyChangeOrganisation) {
          const newOrganisationId = event.payload.stringValues[0];
          if (newOrganisationId) {
            dispatch(
              doSettingOrganisationSelect({
                id: newOrganisationId,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch]);

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Settings"
          showSidebarButton
          // Get help button (mobile only)
          onPressCloseButton={
            !hasSidebarNavigation
              ? () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.Support,
                      params: {},
                    }),
                  )
              : undefined
          }
          closeIcon={ICONS.CommentQuestionLight}
        />
      }
    >
      {organisation && organisation.address ? (
        <GapSmall>
          <Card>
            <CardRow
              onPress={() =>
                dispatch(doSetupOrganisationOpen({ newOrganisationId: null }))
              }
              navigationIsSelected={{
                name: Screens.SETTINGS_ORGANISATION,
                params: {},
              }}
              childrenStyle={{
                alignItems: 'center',
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'center',
              }}
              indentActions
              actionIcon={ICONS.BuildingRefreshRefresh}
              onAction={() => dispatch(doSettingOrganisationSelectStart())}
            >
              <Avatar
                icon={ICONS.HouseBuildingLight}
                data={
                  organisation.svgLogo
                    ? { svgData: organisation.svgLogo }
                    : organisation.pngLogo
                      ? { localUrl: organisation.pngLogo }
                      : null
                }
                size={70}
                imagePadding={7}
                svgPadding={7}
                iconPadding={12}
                style={{ marginRight: theme.padding }}
                imageBackgroundColor={
                  organisation.svgLogo || organisation.pngLogo
                    ? theme.colors.background
                    : undefined
                }
                resizeMode="contain"
              />
              <ItemDetails
                title={organisation.address.name}
                subtitle={[
                  organisation.address.address1,
                  organisation.address.address2,
                  organisation.address.city,
                  organisation.address.county,
                  organisation.address.postcode,
                ]
                  .filter(filterArrayEmpty)
                  .join(', ')}
              />
            </CardRow>
          </Card>
          {(organisation.userFeatures.billingSupport ||
            organisation.userFeatures.billing) && (
            <Card>
              {organisation.userFeatures.billingSupport && (
                <CardRow
                  label="Billing Admin"
                  icon={ICONS.SterlingSignLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupBillingAdminOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsBillingAdmin,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#FE9500',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                  rbs
                />
              )}
              {organisation.userFeatures.billing && (
                <CardRow
                  label="Invoices"
                  icon={ICONS.ReceiptLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupBillingOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SETTINGS_BILLING,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#FE9500',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                />
              )}
            </Card>
          )}
          {(organisation.userFeatures.api ||
            organisation.userFeatures.sso ||
            organisation.userFeatures.engage ||
            canManageIntegrations) && (
            <Card>
              {organisation.userFeatures.api && (
                <CardRow
                  label="API & Webhooks"
                  icon={ICONS.GearComplexCodeLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupApiOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsApi,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#007AFF',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                />
              )}
              {organisation.userFeatures.sso && (
                <CardRow
                  label="SSO & Provisioning"
                  icon={ICONS.PersonWalkingDashedLineArrowRightLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupSsoOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsSso,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#007AFF',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                />
              )}
              {organisation.userFeatures.engage && (
                <CardRow
                  label="Engage"
                  icon={ICONS.PersonLight}
                  iconColor="white"
                  onPress={() =>
                    dispatch(
                      doSetupEngageOpen({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsEngage,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#007AFF',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                />
              )}
              {canManageIntegrations && (
                <CardRow
                  label="Integrations"
                  icon={ICONS.PlugLight}
                  iconColor="white"
                  onPress={() =>
                    navigateScreenAware(
                      Stacks.SETTINGS,
                      Screens.SettingsIntegrations,
                      undefined,
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsIntegrations,
                    params: {},
                  }}
                  iconPressableStyle={{
                    backgroundColor: '#007AFF',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                  rbs
                />
              )}
            </Card>
          )}
          {organisation.userFeatures.fields && (
            <Card>
              <CardRow
                label="Fields"
                icon={ICONS.InputTextDuo}
                onPress={() =>
                  dispatch(
                    doFieldSettingsLoad.request({
                      id: organisation.id,
                    }),
                  )
                }
                navigationIsSelected={{
                  name: Screens.SETTINGS_FIELDS,
                  params: {},
                }}
                iconColor="white"
                iconPressableStyle={{
                  backgroundColor: '#5756D5',
                  marginLeft: 0,
                  marginRight: theme.padding,
                }}
              />
            </Card>
          )}
          {organisation.userFeatures.severities && (
            <Card>
              <CardRow
                label="Severities"
                icon={ICONS.DiamondExclamationSolid}
                onPress={() => dispatch(doSettingsSeveritiesOpen())}
                navigationIsSelected={{
                  name: Screens.SettingsSeverities,
                  params: {},
                }}
                iconColor="white"
                iconPressableStyle={{
                  backgroundColor: '#FE9500',
                  marginLeft: 0,
                  marginRight: theme.padding,
                }}
              />
            </Card>
          )}
          {organisation.userFeatures.assessments && (
            <Card>
              {organisation.userFeatures.elementChecks && (
                <CardRow
                  label="Asset Checks"
                  icon={ICONS.ListCheckLight}
                  iconColor="white"
                  iconPressableStyle={{
                    backgroundColor: '#35C759',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                  onPress={() =>
                    dispatch(
                      doSettingsAseChecksLoad({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsAssessmentCompliance,
                    params: {},
                  }}
                  rbs
                />
              )}
              {organisation.userFeatures.complianceSetup && (
                <CardRow
                  label="Compliance & Assessment Types"
                  icon={ICONS.FlameLight}
                  iconColor="white"
                  iconPressableStyle={{
                    backgroundColor: '#35C759',
                    marginLeft: 0,
                    marginRight: theme.padding,
                  }}
                  onPress={() =>
                    dispatch(
                      doSettingsComplianceAndAssessmentTypesLoad({
                        organisationId: organisation.id,
                      }),
                    )
                  }
                  navigationIsSelected={{
                    name: Screens.SettingsComplianceAndAssessmentTypes,
                    params: {},
                  }}
                />
              )}
              <CardRow
                label="Checklists & Questions"
                icon={ICONS.CommentCheckLight}
                iconColor="white"
                iconPressableStyle={{
                  backgroundColor: '#35C759',
                  marginLeft: 0,
                  marginRight: theme.padding,
                }}
                onPress={() =>
                  dispatch(
                    REMOTE_ASESETUP_LOAD({
                      organisationId: organisation.id,
                    }),
                  )
                }
                navigationIsSelected={{
                  name: Screens.SETTINGS_QUESTIONS,
                  params: {},
                }}
              />
              <CardRow
                icon={ICONS.BallotCheckLight}
                label="Assessment Templates"
                iconColor="white"
                iconPressableStyle={{
                  backgroundColor: '#35C759',
                  marginLeft: 0,
                  marginRight: theme.padding,
                }}
                onPress={() =>
                  dispatch(
                    doSetupAseLoad({
                      organisationId: organisation.id,
                    }),
                  )
                }
                navigationIsSelected={{
                  name: Screens.SETTINGS_ASSESSMENTS,
                  params: {},
                }}
              />
            </Card>
          )}
        </GapSmall>
      ) : (
        <Card label="Organisation">
          <CardRow
            label="Select an organisation..."
            onPress={() => dispatch(doSettingOrganisationSelectStart())}
            icon={ICONS.HouseBuildingLight}
          />
        </Card>
      )}

      {Platform.OS !== 'web' && (
        <Card label="App Settings">
          <CardRow
            label="General"
            onPress={() =>
              navigateScreenAware(
                Stacks.SETTINGS,
                Screens.SettingsGeneral,
                undefined,
              )
            }
            icon={ICONS.SlidersUpLight}
            iconColor="white"
            navigationIsSelected={{
              name: Screens.SettingsGeneral,
              params: {},
            }}
            iconPressableStyle={{
              backgroundColor: theme.colors.blue,
              marginLeft: 0,
              marginRight: theme.padding,
            }}
          />
        </Card>
      )}
    </Page>
  );
};
